import { useRef, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
    OverlayDrawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    Button,
    type DialogOpenChangeEvent,
    type DialogOpenChangeData
} from "@fluentui/react-components";
import { Dismiss24Regular, History24Regular } from "@fluentui/react-icons";
import readNDJSONStream from "ndjson-readablestream";

import styles from "./Chat.module.css";

import {
    accessApi,
    AccessSummary,
    chatApi,
    citationToMkDocsPath,
    citationToSourceRepoUrl,
    configApi,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ChatModelOption,
    ResponseMessage,
    SpeechConfig
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { loadMermaid } from "../../components/Answer/MermaidDiagram";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";
import { UploadFile } from "../../components/UploadFile";
import { useLogin, getToken, requireAccessControl, enableUnauthenticatedAccess, isInIframe, isFirefox, getFirstName } from "../../authConfig";
import { LoginButton } from "../../components/LoginButton/LoginButton";
import { useOidc } from "../../oidcContext";
import { LoginContext } from "../../loginContext";
import { LanguagePicker } from "../../i18n/LanguagePicker";
import { Settings } from "../../components/Settings/Settings";

const WelcomeIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
            fill="currentColor"
        />
    </svg>
);

const HeaderIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
            fill="currentColor"
            opacity="0.85"
        />
    </svg>
);

const ResetIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6a6 6 0 1 1-10.24-4.24L6.34 7.34A8 8 0 1 0 20 13c0-4.42-3.58-8-8-8Z" fill="currentColor" />
    </svg>
);

const CloseIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.3 9.17 12 2.88 5.71 4.29 4.29l6.3 6.3 6.29-6.3 1.42 1.42Z"
            fill="currentColor"
        />
    </svg>
);

const Chat = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(1.9);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [agenticReasoningEffort, setRetrievalReasoningEffort] = useState<string>("minimal");
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useQueryRewriting, setUseQueryRewriting] = useState<boolean>(false);
    const [reasoningEffort, setReasoningEffort] = useState<string>("");
    const [reasoningEffortOptions, setReasoningEffortOptions] = useState<string[]>([]);
    const [gptModel, setGptModel] = useState<string>("");
    const [selectableChatModels, setSelectableChatModels] = useState<ChatModelOption[]>([]);
    const [streamingEnabled, setStreamingEnabled] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const previousShouldStreamRef = useRef<boolean>(true);
    const forcedStreamingRef = useRef<boolean>(false);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [includeCategory, setIncludeCategory] = useState<string>("");
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const [searchTextEmbeddings, setSearchTextEmbeddings] = useState<boolean>(true);
    const [searchImageEmbeddings, setSearchImageEmbeddings] = useState<boolean>(false);
    const [sendTextSources, setSendTextSources] = useState<boolean>(true);
    const [sendImageSources, setSendImageSources] = useState<boolean>(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [restoredQuestion, setRestoredQuestion] = useState<string>("");
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);

    const [showMultimodalOptions, setShowMultimodalOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showQueryRewritingOption, setShowQueryRewritingOption] = useState<boolean>(false);
    const [showReasoningEffortOption, setShowReasoningEffortOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [showSpeechInput, setShowSpeechInput] = useState<boolean>(false);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] = useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const [showAgenticRetrievalOption, setShowAgenticRetrievalOption] = useState<boolean>(false);
    const [webSourceSupported, setWebSourceSupported] = useState<boolean>(false);
    const [webSourceEnabled, setWebSourceEnabled] = useState<boolean>(false);
    const [sharePointSourceSupported, setSharePointSourceSupported] = useState<boolean>(false);
    const [sharePointSourceEnabled, setSharePointSourceEnabled] = useState<boolean>(false);
    const [useAgenticKnowledgeBase, setUseAgenticRetrieval] = useState<boolean>(false);
    const [showOrchestratorOption, setShowOrchestratorOption] = useState<boolean>(false);
    const [showPromptTemplateOption, setShowPromptTemplateOption] = useState<boolean>(false);
    const [useOrchestrator, setUseOrchestrator] = useState<boolean>(false);
    const [hideMinimalRetrievalReasoningOption, setHideMinimalRetrievalReasoningOption] = useState<boolean>(false);
    const streamingDisabledByOverrides = useAgenticKnowledgeBase && webSourceEnabled;

    const audio = useRef(new Audio()).current;
    const [isPlaying, setIsPlaying] = useState(false);

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio,
        isPlaying,
        setIsPlaying
    };

    const getConfig = async () => {
        configApi().then(config => {
            setShowMultimodalOptions(config.showMultimodalOptions);
            if (config.showMultimodalOptions) {
                // Initialize from server config so defaults match deployment settings
                setSendTextSources(config.ragSendTextSources !== undefined ? config.ragSendTextSources : true);
                setSendImageSources(config.ragSendImageSources);
                setSearchTextEmbeddings(config.ragSearchTextEmbeddings);
                setSearchImageEmbeddings(config.ragSearchImageEmbeddings);
            }
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setUseQueryRewriting(config.showQueryRewritingOption);
            setShowQueryRewritingOption(config.showQueryRewritingOption);
            setShowReasoningEffortOption(config.showReasoningEffortOption);
            setReasoningEffortOptions(config.reasoningEffortOptions || []);
            setSelectableChatModels(config.selectableChatModels || []);
            setGptModel(config.defaultChatModel || "");
            setStreamingEnabled(config.streamingEnabled);
            if (config.showReasoningEffortOption) {
                setReasoningEffort(config.defaultReasoningEffort);
            }
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            setShowUserUpload(config.showUserUpload);
            setshowLanguagePicker(config.showLanguagePicker);
            setShowSpeechInput(config.showSpeechInput);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
            setShowAgenticRetrievalOption(config.showAgenticRetrievalOption);
            setUseAgenticRetrieval(config.showAgenticRetrievalOption);
            setShowOrchestratorOption(config.showOrchestratorOption);
            setUseOrchestrator(config.showOrchestratorOption);
            setShowPromptTemplateOption(config.showPromptTemplateOption);
            setWebSourceSupported(config.webSourceEnabled);
            setWebSourceEnabled(config.webSourceEnabled);
            setSharePointSourceSupported(config.sharepointSourceEnabled);
            setSharePointSourceEnabled(config.sharepointSourceEnabled);
            if (config.showAgenticRetrievalOption) {
                setRetrieveCount(10);
            }
            const defaultRetrievalEffort = config.defaultRetrievalReasoningEffort ?? "minimal";
            setHideMinimalRetrievalReasoningOption(config.webSourceEnabled);
            setRetrievalReasoningEffort(defaultRetrievalEffort);
        });
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>, signal: AbortSignal) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {
            output_text: "",
            context: { data_points: { text: [], images: [], citations: [] }, thoughts: [], followup_questions: null },
            session_state: null
        };

        // Buffer incoming deltas and drain them at a steady, character-based pace,
        // one batch per animation frame (~60fps). The stream-reading loop never
        // blocks (no per-token await), so tokens are consumed as fast as the backend
        // sends them into `pendingContent`. A frame-driven pump then reveals a capped
        // number of characters per frame, producing a smooth "typewriter" effect
        // regardless of how bursty the backend deltas are. Once the stream finishes,
        // the remaining buffer is flushed in full so nothing is lost.
        const MAX_CHARS_PER_FRAME = 12; // reveal speed while streaming (~720 chars/sec at 60fps)
        let pendingContent = "";
        let streamDone = false;
        let pumpScheduled = false;
        const renderAnswer = () => {
            const latestResponse: ChatAppResponse = {
                ...askResponse,
                output_text: answer
            };
            setStreamedAnswers([...answers, [question, latestResponse]]);
        };
        const pump = () => {
            pumpScheduled = false;
            if (pendingContent) {
                // While streaming, reveal a capped slice per frame for a steady pace.
                // After the stream is done, drain the whole remaining buffer at once.
                const take = streamDone ? pendingContent.length : Math.min(MAX_CHARS_PER_FRAME, pendingContent.length);
                answer += pendingContent.slice(0, take);
                pendingContent = pendingContent.slice(take);
                renderAnswer();
            }
            // Keep pumping until the buffer is empty, even after the stream ends.
            if (pendingContent) {
                schedulePump();
            }
        };
        const schedulePump = () => {
            if (!pumpScheduled) {
                pumpScheduled = true;
                requestAnimationFrame(pump);
            }
        };
        const updateState = (newContent: string) => {
            pendingContent += newContent;
            schedulePump();
        };
        const flush = () => {
            // Force the remaining buffer out synchronously (used on stream end / abort).
            streamDone = true;
            if (pendingContent) {
                answer += pendingContent;
                pendingContent = "";
                renderAnswer();
            }
        };
        try {
            setIsStreaming(true);
            // Warm up the heavy Mermaid library in the background while the answer
            // text streams in. Diagrams only render once streaming finishes, so by
            // then the library is already loaded and the diagram appears without the
            // one-off lazy-import delay. Fire-and-forget: errors are irrelevant here
            // (the diagram render path handles its own failures) and it reuses the
            // same cached promise as the actual render.
            void loadMermaid().catch(() => {});
            for await (const event of readNDJSONStream(responseBody)) {
                if (signal.aborted) {
                    break;
                }
                if (event["type"] === "response.context" && event["context"] && event["context"]["data_points"]) {
                    askResponse = { ...askResponse, context: event["context"], session_state: event["session_state"] };
                } else if (event["type"] === "response.output_text.delta" && event["delta"] !== undefined) {
                    setIsLoading(false);
                    updateState(event["delta"]);
                } else if (event["type"] === "response.context" && event["context"]) {
                    // Update context with new keys from latest event
                    askResponse.context = { ...askResponse.context, ...event["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // User clicked stop - don't treat as error
                console.log("Stream aborted by user");
            } else {
                throw e; // Re-throw other errors to be caught by makeApiRequest
            }
        } finally {
            // Flush any buffered tail synchronously so no trailing tokens are lost.
            flush();
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            output_text: answer
        };
        return fullResponse;
    };

    const { userManager } = useOidc();
    const client = useLogin ? userManager : undefined;
    const { loggedIn } = useContext(LoginContext);
    const [firstName, setFirstName] = useState<string | null>(null);
    const [access, setAccess] = useState<AccessSummary | undefined>(undefined);

    useEffect(() => {
        if (loggedIn && client) {
            getFirstName(client).then(setFirstName);
        } else {
            setFirstName(null);
        }
    }, [loggedIn, client]);

    useEffect(() => {
        if (useLogin && !loggedIn) {
            setAccess(undefined);
            return;
        }
        (async () => {
            const token = client ? await getToken(client) : undefined;
            setAccess(await accessApi(token));
        })().catch(() => setAccess({ documentation: false, apps: [] }));
    }, [loggedIn, client]);

    const historyProvider: HistoryProviderOptions = (() => {
        if (useLogin && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();
    const historyManager = useHistoryManager(historyProvider);

    const updateStreamingPreference = (isStreamingEnabledOverride: boolean, disablesStreamingOverride: boolean) => {
        if (!isStreamingEnabledOverride) {
            setShouldStream(current => {
                if (!forcedStreamingRef.current) {
                    previousShouldStreamRef.current = current;
                }
                forcedStreamingRef.current = true;
                return current ? false : current;
            });
            return;
        }

        if (disablesStreamingOverride) {
            setShouldStream(current => {
                if (!forcedStreamingRef.current) {
                    previousShouldStreamRef.current = current;
                }
                forcedStreamingRef.current = true;
                return current ? false : current;
            });
            return;
        }

        forcedStreamingRef.current = false;
        setShouldStream(current => {
            const desiredShouldStream = previousShouldStreamRef.current;
            return current === desiredShouldStream ? current : desiredShouldStream;
        });
    };

    const makeApiRequest = async (question: string) => {
        const controller = new AbortController();
        setAbortController(controller);
        lastQuestionRef.current = question;

        error && setError(undefined);
        setRestoredQuestion("");
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const token = client ? await getToken(client) : undefined;

        try {
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].output_text, role: "assistant" }
            ]);

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        include_category: includeCategory.length === 0 ? undefined : includeCategory,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        ...(useAgenticKnowledgeBase ? { retrieval_reasoning_effort: agenticReasoningEffort } : {}),
                        temperature: temperature,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        query_rewriting: useQueryRewriting,
                        reasoning_effort: reasoningEffort,
                        ...(gptModel ? { gpt_model: gptModel } : {}),
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        search_text_embeddings: searchTextEmbeddings,
                        search_image_embeddings: searchImageEmbeddings,
                        send_text_sources: sendTextSources,
                        send_image_sources: sendImageSources,
                        language: i18n.language,
                        use_agentic_knowledgebase: useAgenticKnowledgeBase,
                        use_orchestrator: useOrchestrator,
                        use_web_source: webSourceSupported ? webSourceEnabled : false,
                        use_sharepoint_source: sharePointSourceSupported ? sharePointSourceEnabled : false
                    }
                },
                // AI Chat Protocol: Client must pass on any session state received from the server
                session_state: answers.length ? answers[answers.length - 1][1].session_state : null
            };

            let response = await chatApi(request, shouldStream, token, controller.signal);
            // Safety net: a 403 usually means the access token lapsed between
            // building the request and the backend validating it. Force a silent
            // token renewal and retry the request once before surfacing an error.
            if (response.status === 403 && client) {
                try {
                    await client.signinSilent();
                } catch {
                    // ignore - fall through to the error handling below
                }
                const refreshedToken = await getToken(client);
                response = await chatApi(request, shouldStream, refreshedToken, controller.signal);
            }
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                const errorBody = await response.json().catch(() => null);
                throw Error(errorBody?.error || `Request failed with status ${response.status}`);
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, response.body, controller.signal);
                // Only add to answers if we got content, otherwise restore question to input
                if (parsedResponse.output_text) {
                    setAnswers([...answers, [question, parsedResponse]]);
                    if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                        const token = client ? await getToken(client) : undefined;
                        historyManager.addItem(parsedResponse.session_state, [...answers, [question, parsedResponse]], token);
                    }
                } else {
                    // Stopped before any content arrived - restore question to input
                    lastQuestionRef.current = answers.length > 0 ? answers[answers.length - 1][0] : "";
                    setRestoredQuestion(question);
                }
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw Error(parsedResponse.error);
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    const token = client ? await getToken(client) : undefined;
                    historyManager.addItem(parsedResponse.session_state, [...answers, [question, parsedResponse as ChatAppResponse]], token);
                }
            }
            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Stopped during loading - restore question to input
                lastQuestionRef.current = answers.length > 0 ? answers[answers.length - 1][0] : "";
                setRestoredQuestion(question);
            } else {
                setError(e);
            }
        } finally {
            setIsLoading(false);
            setAbortController(null);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        setRestoredQuestion("");
    };

    // Use block bodies so these effects always return undefined. An implicit
    // arrow return would hand React whatever scrollIntoView() returns as the
    // cleanup function; under some smooth-scroll polyfills / iframe environments
    // that is a non-function value, which React 19 then tries to invoke as
    // destroy() on the next run -> "destroy is not a function" crash.
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    // Preserve streaming preference when agentic retrieval forces streaming off.
    useEffect(() => {
        updateStreamingPreference(streamingEnabled, streamingDisabledByOverrides);
    }, [streamingDisabledByOverrides, streamingEnabled]);

    const handleSettingsChange = (field: string, value: any) => {
        switch (field) {
            case "promptTemplate":
                setPromptTemplate(value);
                break;
            case "temperature":
                setTemperature(value);
                break;
            case "minimumRerankerScore":
                setMinimumRerankerScore(value);
                break;
            case "minimumSearchScore":
                setMinimumSearchScore(value);
                break;
            case "retrieveCount":
                setRetrieveCount(value);
                break;
            case "agenticReasoningEffort": {
                setRetrievalReasoningEffort(value);
                // If selecting minimal while web source is enabled, disable web source
                if (value === "minimal" && webSourceEnabled) {
                    setWebSourceEnabled(false);
                    setHideMinimalRetrievalReasoningOption(false);
                    // Web source was disabled, so restore streaming
                    updateStreamingPreference(streamingEnabled, false);
                }
                break;
            }
            case "useSemanticRanker":
                setUseSemanticRanker(value);
                break;
            case "useQueryRewriting":
                setUseQueryRewriting(value);
                break;
            case "reasoningEffort":
                setReasoningEffort(value);
                break;
            case "gptModel":
                setGptModel(value);
                break;
            case "useSemanticCaptions":
                setUseSemanticCaptions(value);
                break;
            case "excludeCategory":
                setExcludeCategory(value);
                break;
            case "includeCategory":
                setIncludeCategory(value);
                break;
            case "shouldStream":
                {
                    const normalizedShouldStream = !!value;
                    forcedStreamingRef.current = false;
                    previousShouldStreamRef.current = normalizedShouldStream;
                    setShouldStream(normalizedShouldStream);
                }
                break;
            case "useSuggestFollowupQuestions":
                setUseSuggestFollowupQuestions(value);
                break;
            case "llmInputs":
                break;
            case "sendTextSources":
                setSendTextSources(value);
                break;
            case "sendImageSources":
                setSendImageSources(value);
                break;
            case "searchTextEmbeddings":
                setSearchTextEmbeddings(value);
                break;
            case "searchImageEmbeddings":
                setSearchImageEmbeddings(value);
                break;
            case "retrievalMode":
                setRetrievalMode(value);
                break;
            case "useAgenticKnowledgeBase": {
                setUseAgenticRetrieval(value);
                let effectiveWebSource = webSourceEnabled;
                if (!value && webSourceEnabled) {
                    effectiveWebSource = false;
                    setWebSourceEnabled(false);
                    setHideMinimalRetrievalReasoningOption(false);
                }
                // Only web source disables streaming
                const shouldDisableStreaming = !!value && effectiveWebSource;
                updateStreamingPreference(streamingEnabled, shouldDisableStreaming);
                break;
            }
            case "useOrchestrator":
                setUseOrchestrator(value);
                break;
            case "useWebSource":
                if (!webSourceSupported) {
                    setWebSourceEnabled(false);
                    return;
                }
                const normalizedWebSource = !!value;
                setWebSourceEnabled(normalizedWebSource);
                setHideMinimalRetrievalReasoningOption(normalizedWebSource);
                // When enabling web source, disable follow-up questions and streaming
                if (normalizedWebSource) {
                    setUseSuggestFollowupQuestions(false);
                }
                const shouldDisableStreaming = useAgenticKnowledgeBase && normalizedWebSource;
                updateStreamingPreference(streamingEnabled, shouldDisableStreaming);
                break;
            case "useSharePointSource":
                if (!sharePointSourceSupported) {
                    setSharePointSourceEnabled(false);
                    return;
                }
                setSharePointSourceEnabled(!!value);
                break;
        }
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        // In iframe mode, navigate MkDocs parent to the corresponding docs page for .md citations
        if (isInIframe() && citation.endsWith(".md")) {
            const mkdocsPath = citationToMkDocsPath(citation);
            if (mkdocsPath !== null) {
                window.parent.postMessage({ type: "nextchat-navigate", path: mkdocsPath }, "*");
                return;
            }

            // If file is excluded from MkDocs, open source markdown directly in repository
            const sourceRepoUrl = citationToSourceRepoUrl(citation);
            if (sourceRepoUrl) {
                window.open(sourceRepoUrl, "_blank", "noopener,noreferrer");
                return;
            }

            // In iframe mode, never show the citation panel — just ignore unknown files
            return;
        }

        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const onStopClick = async () => {
        try {
            if (abortController) {
                abortController.abort();
            }
        } catch (e) {
            console.log("An error occurred trying to stop the stream: ", e);
        }
    };

    const closeAuxiliaryPanels = () => {
        setIsConfigPanelOpen(false);
        setIsHistoryPanelOpen(false);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
    };

    const { t, i18n } = useTranslation();

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t("pageTitle")}</title>
            </Helmet>
            <div className={styles.chatRoot}>
                <div className={styles.shell}>
                    {!isInIframe() && (
                        <div className={styles.panelHeader}>
                            <div className={styles.panelHeaderTitle}>
                                <HeaderIcon />
                                <span>NextDev AI</span>
                            </div>
                            <div className={styles.panelHeaderRight}>
                                <span className={styles.panelStatus}>Online</span>
                                {useLogin && <LoginButton />}
                                {((useLogin && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                                    <button
                                        className={styles.panelAction}
                                        onClick={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)}
                                        aria-label={t("history.openChatHistory")}
                                        title={t("history.openChatHistory")}
                                    >
                                        <History24Regular />
                                    </button>
                                )}
                                {showUserUpload && (!useLogin || loggedIn) && <UploadFile className={styles.headerUpload} disabled={false} />}
                                <button className={styles.panelAction} onClick={clearChat} aria-label={t("clearChat")}>
                                    <ResetIcon />
                                </button>
                                <button
                                    className={styles.panelAction}
                                    onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)}
                                    aria-label={t("labels.headerText")}
                                    title="Developer Settings"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </button>
                                <button className={styles.panelAction} onClick={closeAuxiliaryPanels} aria-label={t("labels.closeButton")}>
                                    <CloseIcon />
                                </button>
                            </div>
                        </div>
                    )}
                    <div className={styles.chatContainer}>
                        {useLogin && !loggedIn && !enableUnauthenticatedAccess ? (
                            <div className={styles.chatEmptyState}>
                                <div className={styles.chatEmptyStateIcon}>
                                    <HeaderIcon />
                                </div>
                                <h1 className={styles.chatEmptyStateTitle}>NextDev AI</h1>
                                <LoginButton />
                                <p className={styles.loginNotice}>{t("loginFeedbackHint")}</p>
                                <p className={styles.loginNotice}>{t("loginPrivacyHint")}</p>
                                {isInIframe() && isFirefox() && <p className={styles.firefoxAuthHint}>{t("firefoxIframeAuthHint")}</p>}
                            </div>
                        ) : !lastQuestionRef.current ? (
                            <div className={styles.chatEmptyState}>
                                <div className={styles.chatEmptyStateIcon}>
                                    <WelcomeIcon />
                                </div>

                                <h1 className={styles.chatEmptyStateTitle}>
                                    {firstName ? `${t("chatEmptyStateGreeting", { name: firstName })}` : t("chatEmptyStateTitle")}
                                </h1>
                                <h2 className={styles.chatEmptyStateSubtitle}>
                                    {t("chatEmptyStateIntro")} {t("chatEmptyStateSubtitle")}
                                </h2>
                                {showLanguagePicker && <LanguagePicker onLanguageChange={newLang => i18n.changeLanguage(newLang)} />}
                                <ExampleList onExampleClicked={onExampleClicked} useMultimodalAnswering={showMultimodalOptions} access={access} />
                            </div>
                        ) : (
                            <div className={styles.chatMessageStream}>
                                {isStreaming &&
                                    streamedAnswers.map((streamedAnswer, index) => (
                                        <div key={index}>
                                            <UserChatMessage message={streamedAnswer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    // Only the last answer is actually still streaming; earlier
                                                    // answers are complete, so keep them rendered as final (this
                                                    // keeps their mermaid diagrams from reverting to raw code).
                                                    isStreaming={index === streamedAnswers.length - 1}
                                                    key={index}
                                                    answer={streamedAnswer[1]}
                                                    index={index}
                                                    speechConfig={speechConfig}
                                                    isSelected={false}
                                                    onCitationClicked={c => onShowCitation(c, index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                    onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                {!isStreaming &&
                                    answers.map((answer, index) => (
                                        <div key={index}>
                                            <UserChatMessage message={answer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <Answer
                                                    isStreaming={false}
                                                    key={index}
                                                    answer={answer[1]}
                                                    index={index}
                                                    speechConfig={speechConfig}
                                                    isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                    onCitationClicked={c => onShowCitation(c, index)}
                                                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                    onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                    onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                    showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                    showSpeechOutputAzure={showSpeechOutputAzure}
                                                    showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                {isLoading && (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerLoading />
                                        </div>
                                    </>
                                )}
                                {error ? (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                        </div>
                                    </>
                                ) : null}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                        )}

                        <div className={styles.chatInput}>
                            <QuestionInput
                                clearOnSend
                                placeholder={t("defaultExamples.placeholder")}
                                disabled={isLoading}
                                onSend={question => makeApiRequest(question)}
                                showSpeechInput={showSpeechInput}
                                isStreaming={isStreaming}
                                isLoading={isLoading}
                                onStop={onStopClick}
                                initQuestion={restoredQuestion}
                            />
                            <div className={styles.chatDisclaimer}>{t("chatDisclaimer")}</div>
                        </div>
                    </div>
                </div>
                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="750px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                        onCitationClicked={c => onShowCitation(c, selectedAnswer)}
                        onClose={closeAuxiliaryPanels}
                    />
                )}

                {((useLogin && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                    <HistoryPanel
                        provider={historyProvider}
                        isOpen={isHistoryPanelOpen}
                        notify={!isStreaming && !isLoading}
                        onClose={() => setIsHistoryPanelOpen(false)}
                        onChatSelected={answers => {
                            if (answers.length === 0) return;
                            setAnswers(answers);
                            lastQuestionRef.current = answers[answers.length - 1][0];
                        }}
                    />
                )}

                <OverlayDrawer
                    position="end"
                    open={isConfigPanelOpen}
                    className={styles.configDrawer}
                    onOpenChange={(_ev: DialogOpenChangeEvent, { open }: DialogOpenChangeData) => {
                        if (!open) setIsConfigPanelOpen(false);
                    }}
                >
                    <DrawerHeader className={styles.configDrawerHeader}>
                        <DrawerHeaderTitle
                            className={styles.configDrawerHeaderTitle}
                            action={
                                <Button
                                    appearance="subtle"
                                    aria-label={t("labels.closeButton")}
                                    icon={<Dismiss24Regular />}
                                    onClick={() => setIsConfigPanelOpen(false)}
                                />
                            }
                        >
                            {t("labels.headerText")}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody className={styles.configDrawerBody}>
                        <Settings
                            promptTemplate={promptTemplate}
                            temperature={temperature}
                            retrieveCount={retrieveCount}
                            agenticReasoningEffort={agenticReasoningEffort}
                            minimumSearchScore={minimumSearchScore}
                            minimumRerankerScore={minimumRerankerScore}
                            useSemanticRanker={useSemanticRanker}
                            useSemanticCaptions={useSemanticCaptions}
                            useQueryRewriting={useQueryRewriting}
                            reasoningEffort={reasoningEffort}
                            reasoningEffortOptions={reasoningEffortOptions}
                            gptModel={gptModel}
                            selectableChatModels={selectableChatModels}
                            excludeCategory={excludeCategory}
                            includeCategory={includeCategory}
                            retrievalMode={retrievalMode}
                            showMultimodalOptions={showMultimodalOptions}
                            sendTextSources={sendTextSources}
                            sendImageSources={sendImageSources}
                            searchTextEmbeddings={searchTextEmbeddings}
                            searchImageEmbeddings={searchImageEmbeddings}
                            showSemanticRankerOption={showSemanticRankerOption}
                            showQueryRewritingOption={showQueryRewritingOption}
                            showReasoningEffortOption={showReasoningEffortOption}
                            showVectorOption={showVectorOption}
                            useLogin={!!useLogin}
                            loggedIn={loggedIn}
                            requireAccessControl={requireAccessControl}
                            shouldStream={shouldStream}
                            streamingEnabled={streamingEnabled}
                            useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                            showAgenticRetrievalOption={showAgenticRetrievalOption}
                            useAgenticKnowledgeBase={useAgenticKnowledgeBase}
                            showOrchestratorOption={showOrchestratorOption}
                            useOrchestrator={useOrchestrator}
                            showPromptTemplateOption={showPromptTemplateOption}
                            useWebSource={webSourceEnabled}
                            showWebSourceOption={webSourceSupported}
                            useSharePointSource={sharePointSourceEnabled}
                            showSharePointSourceOption={sharePointSourceSupported}
                            hideMinimalRetrievalReasoningOption={hideMinimalRetrievalReasoningOption}
                            onChange={handleSettingsChange}
                        />
                        <div className={styles.configDrawerFooter}>
                            <Button onClick={() => setIsConfigPanelOpen(false)}>{t("labels.closeButton")}</Button>
                        </div>
                    </DrawerBody>
                </OverlayDrawer>
            </div>
        </div>
    );
};

export default Chat;
